import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { IMG } from "@/lib/assets";
import { PageBanner } from "@/components/ui/PageBanner";
import { Button } from "@/components/ui/button";
import { Swords, Shield, Zap, Target, Camera, Shuffle } from "lucide-react";

const KINDS = [
  { key: "all", label: "All shots", icon: Target },
  { key: "attack", label: "Attack", icon: Swords },
  { key: "defence", label: "Defence", icon: Shield },
  { key: "transition", label: "Transition", icon: Shuffle },
  { key: "serve", label: "Serve & Return", icon: Zap },
];

export default function Padel() {
  const navigate = useNavigate();
  const [kind, setKind] = useState("all");
  const { data: shots = [] } = useQuery({
    queryKey: ["padel", kind],
    queryFn: async () => (await api.get("/api/padel/shots", { params: kind !== "all" ? { kind } : {} })).data,
  });

  return (
    <div className="p-4 md:p-8 space-y-6">
      <PageBanner image={IMG.padelHero} overline="sport · padel" title="Padel Academy"
        subtitle="Master every shot with cinematic examples and AVA's coaching cues. Then step onto the court and let AVA watch your technique live."
        actions={<Button onClick={() => navigate("/app/train?mode=padel")} className="bg-acid text-black hover:bg-[#AACC00] rounded-full" data-testid="padel-practice-btn"><Camera className="w-4 h-4" /> Practice with AVA</Button>} />

      <div className="flex gap-2 flex-wrap">
        {KINDS.map((k) => (
          <button key={k.key} onClick={() => setKind(k.key)} data-testid={`padel-kind-${k.key}`}
            className={`px-4 h-10 rounded-full text-sm border transition-colors flex items-center gap-2 ${
              kind === k.key ? "border-acid text-acid bg-acid/10" : "border-[#1E222A] text-[#8A91A6] hover:border-[#343A4A]"}`}>
            <k.icon className="w-4 h-4" /> {k.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {shots.map((s, i) => (
          <motion.div key={s.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
            className="group surface rounded-xl overflow-hidden flex flex-col" data-testid={`shot-card-${i}`}>
            <div className="relative h-52 overflow-hidden">
              <img src={s.image} alt={s.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
              <div className="absolute inset-0" style={{ background: "linear-gradient(0deg,#0F1115 6%,transparent 65%)" }} />
              <span className="absolute top-3 left-3 mono-label px-2.5 py-1 rounded-full bg-black/60 border border-white/10 text-acid capitalize">{s.kind}</span>
              <span className="absolute bottom-3 left-4 font-heading font-black text-2xl tracking-tight">{s.name}</span>
              <span className="absolute bottom-3 right-3 mono-label text-bayes">{s.level}</span>
            </div>
            <div className="p-5 flex-1 flex flex-col">
              <p className="text-[#8A91A6] text-sm">{s.description}</p>
              <div className="mt-3 space-y-1.5">
                {(s.cues || []).map((c) => (
                  <div key={c} className="flex items-start gap-2 text-sm text-white/85">
                    <span className="text-acid mt-1">›</span> {c}
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
